/**
 * Folding a `card-signal` response into what the surface knows about each word on screen.
 *
 * Its own module because `npm test` has no DOM, so the only way to cover this is a pure function —
 * the same reason `nextActiveId` and `termsOnScreen` are exported from `useReadingLog`, and the
 * reason `tokenState` below lives here rather than inside `Sentence.tsx`.
 */

import type { CardCarrier, CardSignalResponse, CardState } from "@/lib/search";

export interface CardSignal {
  state: CardState;
  /** Present only for `elsewhere` — the card that covers this word. */
  cardedAs?: CardCarrier;
}

/**
 * Merge one `card-signal` answer into `current`.
 *
 * **Merge, never replace.** The map also holds terms from earlier batches and the optimistic add in
 * `onAdd`, and rebuilding it from one response would drop both. That is also what makes a late
 * response harmless, and the request cancellation that used to guard this unnecessary.
 *
 * `isOutdatedFor` is the generation guard — the same protection `countGen` gives the `new` counter,
 * which was never extended to this map. A response issued before the user added or undid a card is
 * out of date about exactly that term: it answers `unknown` for one just added, and `known` for one
 * just undone. Landing late, it flipped `✓ in deck` back to `+ add` a second after the add appeared
 * to work.
 *
 * Per term rather than per batch, in both directions. Discarding the whole response instead would
 * lose its other terms' answers, and since a term is only asked about once they would never be
 * re-asked — which is the other half of the bug this replaced.
 *
 * Two rules that were free while this held booleans and are not any more:
 *
 * * **`state` and `cardedAs` are folded into ONE value per term before it is written.** Held apart
 *   — two maps, or two pieces of React state — a term guarded out of one and not the other leaves
 *   a stale "carded as こんにちは" sitting next to a fresh `✓ in deck`.
 * * **`unknown` writes, it does not delete.** Absence means "no answer yet", and the `asked` ref in
 *   `Read.tsx` means a term is only requested once — so deleting would collapse "not carded" and
 *   "never asked" into one state that never gets re-asked.
 */
export function mergeCardSignal(
  current: ReadonlyMap<string, CardSignal>,
  response: CardSignalResponse,
  isOutdatedFor: (term: string) => boolean = () => false,
): Map<string, CardSignal> {
  const next = new Map(current);
  for (const [term, state] of Object.entries(response.state ?? {})) {
    if (isOutdatedFor(term)) continue;
    const cardedAs = response.cardedAs?.[term];
    next.set(term, cardedAs ? { state, cardedAs } : { state });
  }
  return next;
}

const RANK: Record<CardState, number> = { unknown: 0, elsewhere: 1, known: 2 };

/**
 * The state to draw under a token, given the two keys the surface asks about.
 *
 * `termsOnScreen` sends both the dictionary form and the surface, so a token can have two answers —
 * 食べた is unknown as written and known as 食べる. **The stronger wins**, which is what the old
 * `carded.has(a) || carded.has(b)` already meant for two states; the only change is that "stronger"
 * now has three rungs rather than two.
 *
 * A consequence worth naming: `elsewhere` is evaluated for dictionary forms too, and the server
 * cannot tell the two kinds of key apart. So a root you do not hold, whose entry sits on some card,
 * dashes the token. That widens the rule as literally written — and it is the intended reading,
 * because the rule governs what counts as *carded* and this state explicitly does not claim that.
 */
export function tokenState(
  signal: ReadonlyMap<string, CardSignal>,
  token: { surface: string; dictionaryForm: string | null },
): CardState {
  const surface = signal.get(token.surface)?.state ?? "unknown";
  if (!token.dictionaryForm) return surface;
  const root = signal.get(token.dictionaryForm)?.state ?? "unknown";
  return RANK[root] > RANK[surface] ? root : surface;
}
